import { format } from 'date-fns';
import { getImageUrl as getImageUrlUtil } from '@/utils/imageUtils';

export function useApprovalFormatting() {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMMM d, yyyy');
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null || isNaN(Number(price))) {
      return '₱0.00';
    }
    return '₱' + Number(price).toLocaleString('en-PH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatStatus = (status) => {
    if (!status) return '';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatPriority = (priority) => {
    if (!priority) return 'Normal';
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      'draft': 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200',
      'pending': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
      'accepted': 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      'rejected': 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
      'expired': 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
    };
    
    return statusClasses[status] || 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
  };

  const getPriorityClass = (priority) => {
    const priorityClasses = {
      'low': 'px-2 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200',
      'normal': 'px-2 py-1 text-xs font-medium rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      'high': 'px-2 py-1 text-xs font-medium rounded bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
      'urgent': 'px-2 py-1 text-xs font-medium rounded bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
    };
    
    return priorityClasses[priority] || priorityClasses['normal'];
  };

  const getReceiptImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    return getImageUrlUtil(imageUrl);
  };

  // Quotation-specific formatting
  const hasItemDiscounts = (quotation) => {
    if (!quotation?.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
      return false;
    }
    
    return quotation.items.some(item => 
      item.discount_type && 
      item.discount_type !== 'none' && 
      item.discount_value && 
      parseFloat(item.discount_value) > 0
    );
  };

  const calculateTotalItemDiscounts = (quotation) => {
    if (!quotation?.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
      return 0;
    }
    
    return quotation.items.reduce((total, item) => {
      if (!item.discount_type || item.discount_type === 'none' || !item.discount_value) {
        return total;
      }
      
      if (item.discount_type === 'fixed') {
        return total + parseFloat(item.discount_value);
      } else if (item.discount_type === 'percentage') {
        const preDiscountSubtotal = item.quantity * item.unit_price;
        return total + (preDiscountSubtotal * (parseFloat(item.discount_value) / 100));
      }
      
      return total;
    }, 0);
  };

  return {
    formatDate,
    formatPrice,
    formatStatus,
    formatPriority,
    getStatusClass,
    getPriorityClass,
    getReceiptImageUrl,
    hasItemDiscounts,
    calculateTotalItemDiscounts
  };
} 